/**
 * INFINITY LIFE 2.0 — Notification Center Component
 */

import { store } from '../state.js';
import { api, showToast } from '../api.js';
import { requestNotificationPermission } from '../notifications.js';

export function renderNotificationCenter() {
  const { modals, notifications, unreadNotificationsCount } = store.get();

  if (!modals.notifications) {
    return `<div id="notif-center-container"></div>`;
  }

  const hasPushPermission = ('Notification' in window) && Notification.permission === 'granted';

  return `
    <div class="modal-overlay open" id="notif-modal-overlay">
      <div class="modal-card" style="max-width: 500px;">
        <div class="modal-header">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 1.3rem;">🔔</span>
            <h2 class="modal-title">Central de Notificações</h2>
          </div>
          <button class="btn-close" id="btn-close-notif-modal">✕</button>
        </div>

        <!-- Push Permission Banner -->
        ${!hasPushPermission ? `
          <div style="background: rgba(99, 102, 241, 0.1); border: 1px solid rgba(99, 102, 241, 0.3); border-radius: var(--radius-md); padding: 14px; margin-bottom: 18px;">
            <div style="font-weight: 700; font-size: 0.9rem; color: var(--text-primary); margin-bottom: 4px;">
              📲 Receber Lembretes no Dispositivo
            </div>
            <p style="font-size: 0.8rem; color: var(--text-secondary); line-height: 1.4; margin-bottom: 10px;">
              Ative as notificações para receber avisos de reuniões, prazos e lembretes mesmo em segundo plano.
            </p>
            <button class="btn-primary" id="btn-enable-push" style="font-size: 0.8rem; padding: 6px 14px;">
              Ativar Notificações
            </button>
          </div>
        ` : `
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding: 8px 12px; background: rgba(16, 185, 129, 0.1); border: 1px solid rgba(16, 185, 129, 0.25); border-radius: var(--radius-md);">
            <span style="font-size: 0.8rem; color: var(--emerald-accent); font-weight: 600;">✓ Notificações do navegador ativas</span>
            <button class="btn-secondary" id="btn-test-notification" style="font-size: 0.75rem; padding: 4px 8px;">
              Testar Envio
            </button>
          </div>
        `}

        <!-- Actions -->
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px;">
          <span style="font-size: 0.85rem; color: var(--text-muted);">
            ${unreadNotificationsCount > 0 ? `${unreadNotificationsCount} novas notificações` : 'Todas as notificações'}
          </span>
          ${notifications.length > 0 ? `
            <button class="btn-secondary" id="btn-mark-all-read" style="font-size: 0.78rem; padding: 4px 10px;">
              Marcar lidas
            </button>
          ` : ''}
        </div>

        <!-- List -->
        <div style="display: flex; flex-direction: column; gap: 8px; max-height: 400px; overflow-y: auto;">
          ${notifications.length === 0 ? `
            <div style="text-align: center; padding: 40px 0; color: var(--text-muted);">
              <div style="font-size: 2rem; margin-bottom: 8px;">📭</div>
              <p>Nenhuma notificação por enquanto.</p>
            </div>
          ` : `
            ${notifications.map((n) => `
              <div class="notif-item ${!n.is_read ? 'unread' : ''}" data-id="${n.id}" data-link="${n.link || ''}" 
                   style="padding: 12px; border-radius: var(--radius-md); background: ${!n.is_read ? 'rgba(99, 102, 241, 0.08)' : 'var(--bg-tertiary)'}; border: 1px solid ${!n.is_read ? 'rgba(99, 102, 241, 0.3)' : 'var(--border-subtle)'}; cursor: pointer;">
                <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px;">
                  <span style="font-weight: 700; font-size: 0.9rem; color: var(--text-primary);">${escapeHtml(n.title)}</span>
                  ${!n.is_read ? `<span style="width: 8px; height: 8px; border-radius: 50%; background: var(--primary);"></span>` : ''}
                </div>
                <div style="font-size: 0.82rem; color: var(--text-secondary); line-height: 1.4;">
                  ${escapeHtml(n.message)}
                </div>
                <div style="font-size: 0.7rem; color: var(--text-muted); margin-top: 6px;">
                  ${formatDateAgo(n.created_at)}
                </div>
              </div>
            `).join('')}
          `}
        </div>
      </div>
    </div>
  `;
}

function formatDateAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const past = new Date(dateStr);
  const diffSec = Math.floor((now - past) / 1000);

  if (diffSec < 60) return 'Agora mesmo';
  if (diffSec < 3600) return `Há ${Math.floor(diffSec / 60)} min`;
  if (diffSec < 86400) return `Há ${Math.floor(diffSec / 3600)} horas`;
  return dateStr.substring(0, 10);
}

function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

export function attachNotificationEvents(container) {
  const closeBtn = container.querySelector('#btn-close-notif-modal');
  const overlay = container.querySelector('#notif-modal-overlay');

  const close = () => store.toggleModal('notifications', false);

  if (closeBtn) closeBtn.addEventListener('click', close);
  if (overlay) {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
  }

  // Push Permission Request
  const enablePushBtn = container.querySelector('#btn-enable-push');
  if (enablePushBtn) {
    enablePushBtn.addEventListener('click', async () => {
      await requestNotificationPermission();
      window.dispatchEvent(new CustomEvent('infinity:refresh-notifications'));
    });
  }

  // Test Notification
  const testBtn = container.querySelector('#btn-test-notification');
  if (testBtn) {
    testBtn.addEventListener('click', async () => {
      try {
        await api.sendTestNotification();
        showToast('Notificação de teste disparada!', 'success');
        window.dispatchEvent(new CustomEvent('infinity:refresh-notifications'));
      } catch (err) {
        showToast(err.message, 'error');
      }
    });
  }

  // Mark all read
  const markAllBtn = container.querySelector('#btn-mark-all-read');
  if (markAllBtn) {
    markAllBtn.addEventListener('click', async () => {
      try {
        await api.markAllNotificationsRead();
        showToast('Notificações marcadas como lidas.', 'info');
        window.dispatchEvent(new CustomEvent('infinity:refresh-notifications'));
      } catch (err) {
        showToast(err.message, 'error');
      }
    });
  }

  // Click on individual notification
  container.querySelectorAll('.notif-item').forEach((item) => {
    item.addEventListener('click', async () => {
      const id = item.dataset.id;
      const link = item.dataset.link;
      try {
        await api.markNotificationRead(id);
        window.dispatchEvent(new CustomEvent('infinity:refresh-notifications'));
        if (link && link !== '/') {
          store.setView('agenda');
          close();
        }
      } catch (err) {
        // ignore
      }
    });
  });
}

export { attachNotificationEvents as attachNotificationCenterEvents };

